// @ts-nocheck
import PropTypes from "prop-types";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";
import {BaseButton, BaseInput, toast} from "@/components";
import useIsMobile from "@/hooks/useIsMobile";
import eyeActive from "../../assets/images/icon-eye-active.svg";
import eyeInactive from "../../assets/images/icon-eye-inactive.svg";
import googleIcon from "../../assets/images/icon-google.svg";
import useLogin from "../../hooks/useLogin";
import {useAuthStore} from "../../store/useAuth.store";
import {useLogo} from "../../util/logoUtils";

const wrapperCls =
  "relative flex flex-col items-center justify-center w-full max-w-[440px] box-border";
const ambientGlowCls =
  "pointer-events-none absolute -inset-8 -z-0 [background:radial-gradient(circle_at_20%_15%,rgba(250,84,0,0.18),transparent_55%),radial-gradient(circle_at_80%_85%,rgba(215,50,168,0.22),transparent_55%)]";
const cardCls =
  "relative z-[1] w-full rounded-[28px] border border-white/8 bg-[rgba(20,20,28,0.78)] backdrop-blur-2xl px-8 py-10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.04)_inset] max-[480px]:px-6 max-[480px]:py-8";
const closeBtnCls =
  "absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full bg-white/5 border border-white/10 text-[var(--color-text)] text-base cursor-pointer leading-none transition-all duration-200 hover:bg-white/10 hover:scale-105 active:scale-95 focus-visible:outline-2 focus-visible:outline-[rgba(215,50,168,0.6)]";
const formInnerCls = "flex flex-col items-center w-full";
const logoLoginCls = "block mx-auto mb-6 max-w-[var(--size-45)] w-[120px] h-auto drop-shadow-[0_8px_24px_rgba(250,84,0,0.35)]";
const titleCls =
  "text-center mb-1 text-[var(--font-h2)] font-bold text-[var(--color-text)] tracking-tight";
const subtitleCls =
  "text-center mb-7 text-[var(--font-body)] text-[var(--color-text-secondary)]";
const googleButtonCls =
  "!w-full !h-12 gap-3 !rounded-full !border !border-white/12 !bg-white/[0.04] !text-[var(--color-text)] !text-[var(--font-body-lg)] !font-medium !flex !items-center !justify-center !backdrop-blur-md !transition-all !duration-200 hover:!bg-white/[0.08] hover:!border-white/20 hover:!-translate-y-0.5 hover:!shadow-[0_10px_30px_-10px_rgba(0,0,0,0.5)] active:!translate-y-0";
const dividerCls =
  "relative flex items-center gap-3 my-6 w-full text-[var(--font-eyebrow)] uppercase tracking-[0.18em] text-[var(--color-text-tertiary)] before:flex-1 before:h-px before:bg-gradient-to-r before:from-transparent before:to-white/10 after:flex-1 after:h-px after:bg-gradient-to-l after:from-transparent after:to-white/10";
const formContainerLoginCls = "w-full flex flex-col gap-4";
const inputCls =
  "!h-12 !rounded-full !border !border-white/10 !bg-white/[0.03] !text-[var(--color-text)] !px-5 !text-[var(--font-body-lg)] placeholder:!text-[var(--color-text-tertiary)] !transition-all !duration-200 hover:!border-white/20 focus:!border-[rgba(215,50,168,0.7)] focus:!bg-white/[0.05] focus:!shadow-[0_0_0_4px_rgba(215,50,168,0.12)] focus-visible:!outline-none";
const submitBtnCls =
  "!w-full !h-12 !rounded-full !font-bold !text-[var(--font-body-lg)] !bg-[linear-gradient(90deg,rgb(250,84,0),rgb(215,50,168))] !text-white !border-0 !shadow-[0_10px_30px_-8px_rgba(215,50,168,0.55)] !transition-all !duration-200 hover:!-translate-y-0.5 hover:!shadow-[0_14px_34px_-8px_rgba(215,50,168,0.7)] active:!translate-y-0 active:!scale-[0.98]";
const footerLinksCls =
  "flex flex-wrap justify-center items-center gap-x-2 gap-y-3 mt-5 text-[var(--font-body)]";
const forgotLinkCls =
  "cursor-pointer bg-transparent border-0 p-0 font-[var(--font-family)] text-[var(--color-text-secondary)] underline-offset-4 transition-colors duration-200 hover:text-[var(--color-text)] hover:underline";
const noAccountTextCls = "text-[var(--color-text-secondary)]";
const registerLinkCls =
  "cursor-pointer bg-transparent border-0 p-0 ml-1 font-[var(--font-family)] font-bold text-[var(--color-primary)] transition-colors duration-200 hover:opacity-90 hover:underline underline-offset-4";
const errorBoxCls =
  "flex items-start gap-2 rounded-xl border border-[rgba(255,77,77,0.25)] bg-[rgba(255,77,77,0.08)] px-4 py-3 text-[var(--font-body)] text-[var(--color-danger)]";
const floatingLabelMobileCls =
  "text-[var(--font-eyebrow)] text-[var(--color-text-tertiary)] cursor-pointer whitespace-nowrap transition-colors duration-200 hover:text-[var(--color-primary)] bg-transparent border-0 p-0 mb-1.5 self-end";

const SPECIAL_CHAR_REGEX = /[';[(){}¡!"ª°·#$%^&*+=\\|~<>¿?]/;
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;

const buildSchema = t =>
  z.object({
    username: z
      .string()
      .trim()
      .min(1, t("loginEmail"))
      .superRefine((email, ctx) => {
        if (SPECIAL_CHAR_REGEX.test(email)) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_specialChar")});
          return;
        }
        if (!EMAIL_REGEX.test(email)) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_message")});
          return;
        }
        const [localPart, domainPart] = email.split("@");
        if (
          localPart.startsWith(".") ||
          localPart.endsWith(".") ||
          localPart.startsWith("-") ||
          localPart.startsWith("+") ||
          localPart.endsWith("-") ||
          localPart.endsWith("+")
        ) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
          return;
        }
        if (
          localPart.includes("..") ||
          localPart.includes("--") ||
          domainPart.includes("..") ||
          domainPart.includes("--")
        ) {
          ctx.addIssue({code: "custom", message: t("registrationEmail_CharConsecutive")});
          return;
        }
        for (const part of domainPart.split(".")) {
          if (part.startsWith("-") || part.endsWith("-")) {
            ctx.addIssue({code: "custom", message: t("registrationEmail_CharStartEnd")});
            return;
          }
        }
      }),
    password: z.string().min(1, t("loginPassword")),
  });

const PasswordToggle = ({visible, onToggle}) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label="toggle password"
    style={{background: "transparent", border: "none", padding: 0, cursor: "pointer"}}
  >
    <img
      src={visible ? eyeActive : eyeInactive}
      alt="toggle password"
      style={{width: "var(--size-6)", height: "var(--size-6)"}}
    />
  </button>
);

PasswordToggle.propTypes = {
  visible: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
};

const LoginForm = ({onClose}) => {
  const {postLogin, isPending, isError, error} = useLogin();
  const logo = useLogo();
  const {t} = useTranslation();
  const isMobile = useIsMobile();
  const handleAuthForm = useAuthStore(state => state.handleAuthForm);

  const [values, setValues] = useState({username: "", password: ""});
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const firstInputRef = useRef(null);
  const previouslyFocusedRef = useRef(null);

  const schema = useMemo(() => buildSchema(t), [t]);

  // Fix #7: focus management + restore on unmount
  useEffect(() => {
    previouslyFocusedRef.current = typeof document !== "undefined" ? document.activeElement : null;
    const id = window.setTimeout(() => {
      firstInputRef.current?.focus?.();
    }, 50);
    return () => {
      window.clearTimeout(id);
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") {
        prev.focus();
      }
    };
  }, []);

  const handleChange = field => e => {
    const raw = e.target.value;
    // Fix #8: trim email-like field on input to keep visible state == submitted state
    const next = field === "username" ? raw.replace(/\s+/g, "") : raw;
    setValues(v => ({...v, [field]: next}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  // Fix #5: surface real backend error instead of always returning the generic one
  const getErrorMessage = () => {
    const msg = error?.response?.data?.message ?? error?.message;
    if (!msg) return t("loginIncorrectPasswordEmail");
    if (msg.includes("suspendida")) return msg;
    const status = error?.response?.status;
    if (status === 401 || status === 403) return t("loginIncorrectPasswordEmail");
    return msg;
  };

  const handleSubmit = e => {
    e.preventDefault();
    const result = schema.safeParse(values);
    if (!result.success) {
      const nextErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !nextErrors[key]) nextErrors[key] = issue.message;
      }
      setErrors(nextErrors);
      return;
    }
    setErrors({});
    // Fix #6: removed hardcoded testaccount@gmail.com block
    postLogin({username: result.data.username, password: result.data.password});
  };

  // Fix #1: Google sign-in not implemented yet — give explicit feedback instead of dead button
  const handleGoogleSignIn = () => {
    toast.info?.("Inicio con Google estará disponible pronto") ??
      toast("Inicio con Google estará disponible pronto");
  };

  const renderUsernameField = () => (
    <BaseInput
      ref={firstInputRef}
      type="email"
      inputMode="email"
      autoComplete="username"
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      placeholder={t("loginEmailPlaceholder")}
      value={values.username}
      onChange={handleChange("username")}
      error={errors.username}
      className={inputCls}
    />
  );

  const renderPasswordField = placeholder => (
    <BaseInput
      type={showPassword ? "text" : "password"}
      autoComplete="current-password"
      placeholder={placeholder}
      value={values.password}
      onChange={handleChange("password")}
      error={errors.password}
      className={inputCls}
      rightIcon={
        <PasswordToggle
          visible={showPassword}
          onToggle={() => setShowPassword(v => !v)}
        />
      }
    />
  );

  return (
    <div className={wrapperCls}>
      <div className={ambientGlowCls} aria-hidden="true" />
      <div className={cardCls}>
        {onClose && (
          <button
            type="button"
            className={closeBtnCls}
            onClick={onClose}
            aria-label="Cerrar"
          >
            ✕
          </button>
        )}
        <div className={formInnerCls}>
          <img
            src={logo}
            alt="logo"
            className={logoLoginCls}
          />

          <h2 className={titleCls}>{t("loginSessionTitle")}</h2>
          <p className={subtitleCls}>Bienvenido de vuelta</p>

          <BaseButton
            type="button"
            onClick={handleGoogleSignIn}
            leftIcon={
              <img
                src={googleIcon}
                alt="Google"
                style={{width: "var(--size-5)", height: "var(--size-5)"}}
              />
            }
            className={googleButtonCls}
          >
            Inicia sesión con Google
          </BaseButton>

          <div className={dividerCls} role="separator">
            <span>o con tu correo</span>
          </div>

          <form
            className={formContainerLoginCls}
            onSubmit={handleSubmit}
            autoComplete="on"
            noValidate
          >
            {renderUsernameField()}

            {isMobile && (
              <button
                type="button"
                className={floatingLabelMobileCls}
                onClick={() => handleAuthForm("recover")}
              >
                {t("loginForgotPassword")}
              </button>
            )}

            {renderPasswordField(t("loginPasswordPlaceholder"))}

            {isError && (
              <div className={errorBoxCls} role="alert">
                <span aria-hidden="true">⚠</span>
                <span>{getErrorMessage()}</span>
              </div>
            )}

            <BaseButton
              type="submit"
              variant="primary"
              loading={isPending}
              className={submitBtnCls}
            >
              {t("loginSendBtn")}
            </BaseButton>

            <div className={footerLinksCls}>
              {!isMobile && (
                <button
                  type="button"
                  onClick={() => handleAuthForm("recover")}
                  className={forgotLinkCls}
                >
                  {t("loginForgotPassword")}
                </button>
              )}
              <span className={noAccountTextCls}>
                {t("loginNoAccount")}
                <button
                  type="button"
                  onClick={() => handleAuthForm("registration")}
                  className={registerLinkCls}
                >
                  {t("loginRegister")}
                </button>
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

LoginForm.propTypes = {
  onClose: PropTypes.func,
};

export default LoginForm;
